import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TableComponent } from '../../../../shared/component/table/table.component';
import { AgGridTableService } from '../../../../shared/services/ag-grid.service';
import { ReportPaginationService, ReportPayload } from '../../Reports/report-pagination.service';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-evc-monthly-consumption',
  standalone: true,
  imports: [CommonModule, TableComponent],
  providers: [AgGridTableService, ReportPaginationService],
  template: `
    <div class="evc-section">
      <div class="evc-sec-head">
        <div class="evc-sec-title">
          <span class="evc-sec-accent"></span>
          <div>
            <span class="evc-sec-name">EV Monthly Consumption Report&nbsp;</span>
            <span class="evc-sec-sub">Grid vs DG consumed unit distribution</span>
          </div>
        </div>
        <div class="view-toggle">
          <button class="vt-btn" [class.vt-active]="viewMode === 'grid'" (click)="setView('grid')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> Grid
          </button>
          <button class="vt-btn" [class.vt-active]="viewMode === 'chart'" (click)="setView('chart')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> Chart
          </button>
        </div>
      </div>
      <div class="evc-content-area">
        <div class="evc-grid-wrap" [class.view-hidden]="viewMode !== 'grid'">
           <app-table 
             [pageNation]="false" 
             [DateFilterShow]="true" 
             [mode]="'month'"
             [paginationPageSizeSelector]="[10, 20, 50]" 
             [maxRowCount]="totalCount" 
             (onPageChange)="handlePageChange($event)" 
             [ExportFileName]="exportFileName"
             (filtersApplied)="onFiltersApplied($event)">
           </app-table>
        </div>
        <div class="evc-chart-wrap" [class.view-hidden]="viewMode !== 'chart'">
          <canvas #chartCanvas class="evc-canvas"></canvas>
        </div>
      </div>
    </div>
  `,
  styleUrl: '../ev-charger.component.scss'
})
export class EvcMonthlyConsumptionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locationId: string = '';
  @Input() siteId: string = '';
  @Input() evName?: string;

  @ViewChild('chartCanvas', { static: false }) chartCanvasRef?: ElementRef<HTMLCanvasElement>;

  viewMode: 'grid' | 'chart' = 'grid';
  chartInstance?: Chart;
  private rowData: any[] = [];
  fromDate: string = '';
  toDate: string = '';
  totalCount: number = 0;

  get exportFileName(): string {
    const namePart = this.evName ? ` ${this.evName}` : '';
    return `EV Monthly Consumption Report${namePart} ${new Date().toISOString().slice(0, 10)}`;
  }

  private destroy$ = new Subject<void>();
  private isDestroyed = false;
  private renderTimer: any = null;

  constructor(
    private tableService: AgGridTableService,
    public reportPaginationService: ReportPaginationService,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    if (!this.locationId) {
      this.locationId = localStorage.getItem('login_id') || localStorage.getItem('location_id') || '9440991006';
    }
    if (!this.siteId) {
      this.siteId = sessionStorage.getItem('consumerSiteId') || localStorage.getItem('selected_site_id') || localStorage.getItem('site_idLogin') || localStorage.getItem('site_id') || '59.64062087';
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');

    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevYear = prevDate.getFullYear();
    const prevMonth = String(prevDate.getMonth() + 1).padStart(2, '0');

    this.fromDate = `${prevYear}-${prevMonth}`;
    this.toDate = `${currentYear}-${currentMonth}`;

    this.reportPaginationService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.totalCount = count;
      });

    this.tableService.tableData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (this.isDestroyed) return;
        this.rowData = (state?.rowData || []).filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
        if (this.viewMode === 'chart' && this.rowData.length > 0) {
          if (this.renderTimer) clearTimeout(this.renderTimer);
          this.renderTimer = setTimeout(() => this.renderChart(), 80);
        }
      });

    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    const locChange = changes['locationId'];
    const siteChange = changes['siteId'];
    if ((locChange && !locChange.firstChange && locChange.currentValue) ||
      (siteChange && !siteChange.firstChange && siteChange.currentValue)) {
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
  }

  setView(mode: 'grid' | 'chart') {
    this.viewMode = mode;
    if (mode === 'chart') {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.renderChart(), 80);
    }
  }

  onFiltersApplied(event: { from_date: string; to_date: string; site_id?: string; date?: string }) {
    if (event.from_date && event.to_date) {
      this.fromDate = event.from_date;
      this.toDate = event.to_date;
      if (event.site_id) {
        this.siteId = event.site_id;
      }
      this.loadData();
    }
  }

  handlePageChange(event: { page: number; pageSize: number }) {
    this.reportPaginationService.changePage(event, this.destroy$);
  }

  loadData() {
    const servicePayload: ReportPayload = {
      from_date: this.fromDate,
      to_date: this.toDate,
      site_ids: this.siteId,
      loc_id: this.locationId,
      date: this.toDate || this.fromDate,
      is_paginate: 'N',
    };

    this.reportPaginationService.initFilters(
      servicePayload,
      'MONTHLY_REPORT',
      this.destroy$,
      '',
      'Reports',
      null,
      false
    );
  }

  renderChart() {
    if (this.isDestroyed) return;
    const canvas = this.chartCanvasRef?.nativeElement;
    if (!canvas || !canvas.isConnected) return;
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }

    const nonDummyRows = this.rowData.filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
    if (nonDummyRows.length === 0) return;
    const row = nonDummyRows[0] || {};
    const monthName = row['date'] || row['month'] || row['Month'] || 'Selected Month';
    const gridConsumed = parseFloat(row['unit_grid_consumed'] ?? row['unit_grid_closing'] ?? 0);
    const dgConsumed = parseFloat(row['unit_dg_consumed'] ?? row['unit_dg_closing'] ?? 0);

    this.ngZone.runOutsideAngular(() => {
      if (this.isDestroyed || !canvas.isConnected) return;
      this.chartInstance = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: ['Grid Unit Consumed', 'DG Unit Consumed'],
          datasets: [
            {
              data: [gridConsumed, dgConsumed],
              backgroundColor: [
                'rgba(34, 197, 94, 0.85)',
                'rgba(245, 158, 11, 0.85)'
              ],
              borderColor: [
                '#22c55e',
                '#f59e0b'
              ],
              borderWidth: 2,
              hoverOffset: 8,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '68%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                boxWidth: 14,
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 20
              }
            },
            title: {
              display: true,
              text: `${monthName} - Consumed Units Distribution`,
              font: {
                size: 14,
                weight: 'bold'
              },
              padding: {
                bottom: 12
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  const total = gridConsumed + dgConsumed;
                  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                  return ` ${label}: ${value.toLocaleString()} kWh (${pct}%)`;
                }
              }
            }
          }
        }
      });
    });
  }
}
